<template lang="pug">
  v-container(fluid)
    v-snackbar( v-model="showSnackBar" :color="snackBarColor" ) {{snackBarMessage}}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="showSnackBar = false")
          v-icon fal fa-times

    DetailImageDialog(
      v-if="showDetailImageDialog"
      :show="showDetailImageDialog"
      :foodImageList="foodImageList"
      :index="indexItem"
      :selectedFood="selectedFood"
      @close="onCloseDetailImageClick")
    input.inputFile(type="file" ref="fileInput" accept=".jpeg, .jpg" @change="onFileChange" multiple)

    v-row.ma-0(v-if="$vuetify.breakpoint.mdAndUp")
      v-col(cols="12")
        v-btn(@click="onAddImageClick")
          v-icon(medium) fas fa-image
          span.ml-2 {{ $t('foodDatabasePage.addNewImage') }}
      v-col(cols="12")
        v-list.text-center( v-if="foodImageList.length < 1") {{ $t('foodDatabasePage.noImagesTitle') }}
        v-list(v-else)
          draggable.row(
            v-if="foodImageList.length && !isSaveInProgress || !deleteInProgress"
            ghost-class="grey"
            v-model="foodImageList"
            :disabled="isSaveInProgress"
            @change="updateImageList"
            draggable=".enableDrag")
              v-col.image-wrapper( md="4" v-for="(item, idx) in foodImageList" :key="idx" :class="getDraggableClass(item)")
                FoodImage(
                  :disabledRemoveButtons="disabledRemoveButtons"
                  :idx="idx"
                  :image="item"
                  :selectedFood="selectedFood"
                  @savedImage="savedImage"
                  @reloadImage="reloadImage"
                  @removeImage="removeImage"
                  @showDetailDialog="onDetailImageClick"
                  @showError="showImageError"
                  @isDeleting="isDeleting")

    v-row.ma-0(v-if="$vuetify.breakpoint.smAndDown")
      v-col.pa-0(cols="12")
        v-btn(@click="onAddImageClick")
          v-icon(medium) fas fa-image
          span.ml-2 {{ $t('foodDatabasePage.addNewImage') }}
      v-col(cols="12" )
        v-list.text-center( v-if="foodImageList.length < 1") {{ $t('foodDatabasePage.noImagesTitle') }}
        v-list(v-else)
          draggable(
            :disabled="isSaveInProgress"
            v-if="foodImageList.length && !isSaveInProgress || !deleteInProgress"
            ghost-class="grey"
            handle=".handle"
            v-model="foodImageList"
            @change="updateImageList"
            draggable=".enableDrag")
              v-col.image-wrapper.pb-3.text-center.pa-0(cols="12" class="handle"  v-for="(item,idx) in foodImageList" :key="idx" :class="getDraggableClass(item)")
                FoodImage(
                  :disabledRemoveButtons="disabledRemoveButtons"
                  :idx="idx"
                  :image="item"
                  :selectedFood="selectedFood"
                  @savedImage="savedImage"
                  @reloadImage="reloadImage"
                  @removeImage="removeImage"
                  @showDetailDialog="onDetailImageClick"
                  @isDeleting="isDeleting")
</template>

<script lang="ts">
import {Component, Prop} from "vue-property-decorator";
import FoodItem from "@/model/food-database/FoodItem";
import i18n from "@/plugins/i18n";
import {TranslateResult} from "vue-i18n";
import {
  FETCH_FOOD_ITEM_IMAGES,
  UPDATE_FOOD_ITEM_IMAGE
} from "@/store/action-types";
import Vue from "vue";
import {Action} from "vuex-class";
import draggable from "vuedraggable";
import index from "vuex";
import DetailImageDialog from "@/components/dailymenu/detail/DetailImageDialog.vue";
import {UpdateImageList} from "@/types/payload";
import FoodImage from "@/components/dailymenu/FoodImage.vue";
import FoodImageModel from "@/model/food-database/FoodImageModel";

function valueIsRequired (v: string): boolean | TranslateResult {
  return v.length > 0 || i18n.t('validation.valueIsRequired')
}

interface EventFile extends Event {
  target: HTMLInputElement & EventTarget
}


@Component({
  computed: {
    index() {
      return index
    }
  },
  components: {
    draggable,
    DetailImageDialog,
    FoodImage
  }
})

export default class FoodImageGallery extends Vue {

  $refs!: {
    fileInput: HTMLInputElement
  }

  @Prop({type: Object as () => FoodItem}) selectedFood!: FoodItem
  @Action(FETCH_FOOD_ITEM_IMAGES) fetchFoodItemImageList!: ({ (payload: any): Promise<any> })
  @Action(UPDATE_FOOD_ITEM_IMAGE) updateFoodItemImage!: ({ (payload: UpdateImageList): Promise<any> })

  private showSnackBar: boolean = false
  private snackBarColor: string = ''
  private snackBarMessage: TranslateResult | null = null
  private isSaveInProgress: boolean = false
  private fetchInProgress: boolean = false
  private foodImageList: FoodImageModel[] = []
  private saveImage: boolean = false
  private updateImageOrder: boolean = false
  private showDetailImageDialog: boolean = false
  private indexItem: number | null = null
  private deleteInProgress: boolean = false
  private disabledRemoveButtons: boolean = false

  getDraggableClass (item: FoodImageModel): string {
    if(item.image.includes('/api')) {
      return 'enableDrag'
    } else {
      return ''
    }
  }

  onDetailImageClick(item: number): void {
    this.showDetailImageDialog = true
    this.indexItem = item
  }

  isDeleting (value: boolean): void {
    this.disabledRemoveButtons = value
  }

  onCloseDetailImageClick(): void {
    this.showDetailImageDialog = false
  }

  onAddImageClick (): void {
    if (this.$refs.fileInput) {
      //@ts-ignore
      this.$refs.fileInput.click()
    }
  }

  updateImageList () {
    this.updateImageOrder = true
    this.updateFoodImageList()
  }

  onFileChange (e: EventFile): void {
    if (e && e.target.files) {
      const files: FileList | undefined = e.target.files

      if (files) {
        for (let i = 0; i < files.length; i++) {
          const reader = new FileReader()

          reader.readAsDataURL(files[i])
          reader.onload = (e: any) => {
            if (e.target) {

              const img = new Image()
            // @ts-ignore
            img.src = e.target.result
            img.onload = () => {
              const elem = document.createElement('canvas')
              const scaleFactor = 1280 / img.width
              elem.width = 1280
              elem.height = img.height * scaleFactor
              const ctx = elem.getContext('2d')

              ctx!.drawImage(img, 0, 0, 1280, img.height * scaleFactor)
              ctx!.canvas.toBlob((blob: any) => {
                new File([blob], 'image', {
                  type: 'image/jpeg' || 'image/jpg',
                  lastModified: Date.now()
                })
              }, 'image/jpeg' || 'image/jpg', 1)
              const image: string = elem.toDataURL('image/jpeg' || 'image/jpg', 1)
              this.foodImageList.push(FoodImageModel.create(image))
              this.saveImage = true
              this.$refs.fileInput.value = ''
            }
            }
          }
        }
      }
    }
  }

  savedImage (image: FoodImageModel, imageId: string): void {
    const index: number = this.foodImageList.indexOf(image)
    this.foodImageList.splice(index, 1, FoodImageModel.create(`/api/food-database/food-item-media/${imageId}`))
    this.snackBarColor = "success"
    this.snackBarMessage = this.$t('foodDatabasePage.foodItemImageWasCreatedMessage')
    this.showSnackBar = true
  }

  reloadImage (image: FoodImageModel, imageId: string): void {
    const index: number = this.foodImageList.indexOf(image)
    this.foodImageList.splice(index, 1, FoodImageModel.create(`/api/food-database/food-item-media/${imageId}`))
    this.snackBarColor = "success"
    this.snackBarMessage = this.$t('foodDatabasePage.foodItemWasCreatedMessage')
    this.showSnackBar = true
  }

  removeImage (image: FoodImageModel): void {
    this.$nextTick(() => {
      const index: number = this.foodImageList.indexOf(image)
      this.foodImageList.splice(index, 1)
      this.snackBarColor = "success"
      this.snackBarMessage = this.$t('foodDatabasePage.foodItemImageWasDeletedMessage')
      this.showSnackBar = true
    })
  }

  showImageError (text: string): void {
    this.snackBarColor = "error"
    this.snackBarMessage = text
    this.showSnackBar = true
  }

  async updateFoodImageList (): Promise<any> {
    let promiseList: Promise<void>[] = []
    this.foodImageList.forEach((item: FoodImageModel, idx: number) => {
      if(item.image.includes('/api')) {
        const payload: UpdateImageList = {
          id: item.image,
          order: idx + 1
        }
        promiseList.push(this.updateFoodItemImage(payload))
      }
    })
    this.fetchInProgress = true
    this.$emit('closeButtonDisabled', true)
    try {
      await Promise.all(promiseList)
      this.$emit('closeButtonDisabled', false)
      this.showSnackBar = true
      this.snackBarColor = "success"
      this.snackBarMessage = this.$t('foodDatabasePage.foodItemImageDragMessage')
    } catch (e) {
      this.snackBarColor = "error"
      this.snackBarMessage = this.$t('foodDatabasePage.foodItemImageDragErrorMessage')
      this.showSnackBar = true
    } finally {
    }
  }

  mounted(): void {
    this.foodImageList = FoodImageModel.createList(this.selectedFood.media)
  }
}
</script>

<style lang="stylus" scoped>

.inputFile
  display none

.image-wrapper
  position relative

.remove-button
  position: absolute;
  top: 8px;
  right: 3px;
  z-index: 100;
  background-color: darkgray;
  //padding: 4px 3px;

  color: #000;
  font-weight: bold;
  cursor: pointer;

.remove-button1
  position: absolute;
  top: 5px;
  right: 3px;
  z-index: 100;
  background-color: darkgray;
  //padding: 4px 3px;

  color: #000;
  font-weight: bold;
  cursor: pointer;
.image-shadow
  filter:grayscale(130%)
.reload-button-position
  margin-top -240px
  margin-left 22px
  z-index 200

.reload-button-position2
  margin-top -240px
  margin-left 5px
  z-index 200

.pointer
  cursor pointer
</style>